import { useState, useEffect, useMemo } from 'react';

/**
 * Hook pentru gestionarea automată a prețurilor și timer-ului
 * bazat pe calendarul de scumpiri
 */
export const usePricingCalendar = (pricingTiers) => {
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [nextPeriod, setNextPeriod] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  // Update current date every minute to detect period changes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // Calculate current and next period
  const periods = useMemo(() => {
    if (!pricingTiers || pricingTiers.length === 0) {
      return { current: null, next: null };
    }

    const today = currentDate;
    let current = null;
    let next = null;

    // Find current period
    for (let i = 0; i < pricingTiers.length; i++) {
      const tier = pricingTiers[i];
      const start = new Date(tier.startDate);
      const end = new Date(tier.endDate);
      // Set end date to end of day (23:59:59)
      end.setHours(23, 59, 59, 999);

      if (today >= start && today <= end) {
        current = tier;
        // Next period is the one after current
        if (i < pricingTiers.length - 1) {
          next = pricingTiers[i + 1];
        }
        break;
      } else if (today < start) {
        // We're before this period, so this is the next period
        next = tier;
        break;
      }
    }

    // If no current period found, use first period as next
    if (!current && !next && pricingTiers.length > 0) {
      next = pricingTiers[0];
    }

    // If we're past all periods, current is the last one
    if (!current && pricingTiers.length > 0) {
      current = pricingTiers[pricingTiers.length - 1];
    }

    return { current, next };
  }, [pricingTiers, currentDate]);

  // Update state when periods change
  useEffect(() => {
    setCurrentPeriod(periods.current);
    setNextPeriod(periods.next);
  }, [periods]);

  // Get timer target date (end of current period, or start of next period)
  const timerTargetDate = useMemo(() => {
    if (currentPeriod) {
      const endDate = new Date(currentPeriod.endDate);
      endDate.setHours(23, 59, 59, 999);
      return endDate;
    }
    if (nextPeriod) {
      const startDate = new Date(nextPeriod.startDate);
      startDate.setHours(0, 0, 0, 0);
      return startDate;
    }
    // Fallback: last period end date
    if (pricingTiers && pricingTiers.length > 0) {
      const lastPeriod = pricingTiers[pricingTiers.length - 1];
      const endDate = new Date(lastPeriod.endDate);
      endDate.setHours(23, 59, 59, 999);
      return endDate;
    }
    return null;
  }, [currentPeriod, nextPeriod, pricingTiers]);

  // Get current prices
  const currentPrices = useMemo(() => {
    if (currentPeriod) {
      return {
        standard: currentPeriod.standard,
        vip: currentPeriod.vip,
        ultraVip: currentPeriod.ultraVip,
      };
    }
    // If no current period, use first period prices
    if (pricingTiers && pricingTiers.length > 0) {
      const firstPeriod = pricingTiers[0];
      return {
        standard: firstPeriod.standard,
        vip: firstPeriod.vip,
        ultraVip: firstPeriod.ultraVip,
      };
    }
    return null;
  }, [currentPeriod, pricingTiers]);

  // Calculate discounts from last period
  const discounts = useMemo(() => {
    if (!pricingTiers || pricingTiers.length === 0 || !currentPrices) {
      return { standard: 0, vip: 0, ultraVip: 0 };
    }

    const lastPeriod = pricingTiers[pricingTiers.length - 1];
    const standardDiscount = Math.round(
      ((lastPeriod.standard - currentPrices.standard) / lastPeriod.standard) * 100
    );
    const vipDiscount = Math.round(
      ((lastPeriod.vip - currentPrices.vip) / lastPeriod.vip) * 100
    );
    const ultraVipDiscount = Math.round(
      ((lastPeriod.ultraVip - currentPrices.ultraVip) / lastPeriod.ultraVip) * 100
    );

    return {
      standard: standardDiscount,
      vip: vipDiscount,
      ultraVip: ultraVipDiscount,
    };
  }, [pricingTiers, currentPrices]);

  return {
    currentPeriod,
    nextPeriod,
    timerTargetDate,
    currentPrices,
    discounts,
    isPeriodActive: currentPeriod !== null,
  };
};












